import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { BarChart2, ChevronRight, Loader2, AlertCircle, Calendar, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ParentNav from '@/components/ParentNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const moodClass = (mood = '') => {
  if (['happy', 'excited', 'very_happy'].includes(mood)) return 'bg-green-100 text-green-700';
  if (mood === 'calm') return 'bg-blue-100 text-blue-700';
  if (['curious', 'engaged'].includes(mood)) return 'bg-violet-100 text-violet-700';
  if (['upset', 'anxious', 'sad'].includes(mood)) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-700';
};

export default function ParentMonthlyReportsList() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (!token) { navigate('/parent/login'); return; }
    axios.get(`${BACKEND_URL}/api/parent/monthly-reports`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => setReports(r.data.reports || []))
      .catch(() => setError('Could not load monthly reports.'))
      .finally(() => setLoading(false));
  }, [navigate]);

  // Group reports by child name
  const byChild = reports.reduce((acc, r) => {
    const key = r.child_name || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-rose-50">
      <ParentNav />

      <div className="pt-20 pb-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Header — matches Daily Insights style */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/parent/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart2 className="w-6 h-6 text-orange-500" />
                  Monthly Reports
                </h1>
                <p className="text-gray-600 text-sm">Month-by-month insights shared by your child's observer</p>
              </div>
            </div>
          </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 rounded-xl border border-red-100 text-red-600">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div className="text-center py-20">
            <BarChart2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">No monthly reports yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Your child's observer will share monthly insights here once sessions are complete.
            </p>
          </div>
        )}

        {!loading && !error && Object.entries(byChild).map(([childName, childReports]) => (
          <div key={childName} className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {childName.charAt(0)}
              </div>
              <h2 className="text-lg font-semibold text-gray-800">{childName}</h2>
              <span className="text-xs text-gray-400">{childReports.length} report{childReports.length !== 1 ? 's' : ''}</span>
            </div>

            <div className="space-y-3">
              {childReports.map(r => {
                const mood = r.metadata?.dominant_mood || 'neutral';
                const eng = r.metadata?.avg_engagement;
                return (
                  <div
                    key={r.id}
                    onClick={() => navigate(`/parent/monthly-report/${r.id}`)}
                    className="cursor-pointer group"
                  >
                    <Card className="border-0 shadow-sm group-hover:shadow-md transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center flex-shrink-0">
                              <Calendar className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{r.month_label}</p>
                              <p className="text-sm text-gray-500 mt-0.5">
                                {r.report_count} session{r.report_count !== 1 ? 's' : ''}
                                {r.date_range?.first && ` · ${r.date_range.first} → ${r.date_range.last}`}
                              </p>
                              <div className="flex flex-wrap gap-2 mt-2">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${moodClass(mood)}`}>
                                  {mood} mood
                                </span>
                                {eng != null && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${eng >= 60 ? 'bg-green-100 text-green-700' : eng >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                                    {eng}/100 engagement
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-orange-400 group-hover:text-orange-600 transition-colors flex-shrink-0" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
